import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "../utils/api";
import { AppUser } from "../types";

// Два независимых пуша в приложении — задачи подбора (брокер) и непрочитанные
// события по заявкам СНС (решения банков, допзапросы) — опрос, бейджи в меню,
// всплывающий тост. Вынесено из App.tsx: было 55+ строк состояния/эффектов,
// не связанных с остальной логикой экрана.
export function useNotifications(currentUser: AppUser | null) {
  const [myTaskCount, setMyTaskCount] = useState(0);
  const [taskToast, setTaskToast] = useState<string | null>(null);
  const [toastTone, setToastTone] = useState<api.EventTone>("neutral");   // цвет тоста: зелёный — хорошая новость
  const [toastView, setToastView] = useState<"broker" | "applications">("broker");   // куда ведёт «открыть» в тосте
  const prevTaskCount = useRef<number | null>(null);
  useEffect(() => {
    if (!currentUser) { prevTaskCount.current = null; setMyTaskCount(0); return; }
    let alive = true;
    const tick = async () => {
      try {
        const list = await api.fetchBrokerTasks("mine");
        if (!alive) return;
        const n = list.length;
        if (prevTaskCount.current != null && n > prevTaskCount.current) {
          setTaskToast(`Новая задача подбора · всего ${n}`); setToastTone("neutral"); setToastView("broker");
          setTimeout(() => setTaskToast(null), 6000);
        }
        prevTaskCount.current = n;
        setMyTaskCount(n);
      } catch { /* тихо: уведомления не критичны */ }
    };
    tick();
    // В фоне не опрашиваем (батарея, трафик телефона); вернулись — сразу сверяемся (P2-08).
    const id = setInterval(() => { if (!document.hidden) tick(); }, 60000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [currentUser]);
  // Заявки СНС: непрочитанные события (решения банков, допзапросы) — бейдж в меню и тост.
  const [appsUnseen, setAppsUnseen] = useState({ count: 0, attention: 0 });
  const [appRef, setAppRef] = useState<string | null>(null);
  const prevAppEvent = useRef<number | null>(null);
  const pollApps = useCallback(async () => {
    try {
      const u = await api.getApplicationsUnseen();
      setAppsUnseen({ count: u.count, attention: u.attention });
      if (prevAppEvent.current != null && u.lastId && u.lastId > prevAppEvent.current && u.lastTitle) {
        setTaskToast(`${u.lastCompany ? u.lastCompany + " — " : ""}${u.lastTitle}`);
        setToastTone(u.lastTone || "neutral");
        setToastView("applications");
        setTimeout(() => setTaskToast(null), 8000);
      }
      if (u.lastId) prevAppEvent.current = u.lastId;
      else if (prevAppEvent.current == null) prevAppEvent.current = 0;
    } catch { /* уведомления не критичны */ }
  }, []);
  useEffect(() => {
    if (!currentUser) { prevAppEvent.current = null; setAppsUnseen({ count: 0, attention: 0 }); return; }
    pollApps();
    // Вебхук СНС приходит на сервер сразу; на экране событие появляется при ближайшем опросе —
    // раз в 15 с и сразу при возврате в приложение.
    const id = setInterval(() => { if (!document.hidden) pollApps(); }, 15000);
    const onVis = () => { if (!document.hidden) pollApps(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", onVis); };
  }, [currentUser, pollApps]);

  return {
    myTaskCount, taskToast, setTaskToast, toastTone, toastView,
    appsUnseen, appRef, setAppRef, pollApps,
  };
}
